import { useCallback, useEffect, useState } from 'react';
import { hexToHsba, hsbaToHex } from '../utils/color';
import { ColorPicker, Popover, TextField, type HSBAColor } from '@shopify/polaris';

type Props = {
    value: string;
    onChange: (value: string) => void;
    title?: string;
};

export function CustomColorPicker({ value, onChange, title = '' }: Readonly<Props>) {
    const [popoverActive, setPopoverActive] = useState(false);
    const [textColor, setTextColor] = useState(value);

    const togglePopoverActive = useCallback(() => setPopoverActive(popoverActive => !popoverActive), []);

    const handleTextFieldChange = (color: string) => {
        if (!/^#[0-9A-Fa-f]{0,8}$/.test(color)) {
            return;
        }

        if (/^#([0-9A-Fa-f]{2}){3,4}$/.test(color)) {
            onChange(color);
        }

        setTextColor(color);
    };

    const activator = (
        <div
            onClick={togglePopoverActive}
            style={{
                cursor: 'pointer',
                background: value,
                width: 64,
                height: 32,
                borderRadius: 6,
                border: '1px solid #000000',
            }}
        ></div>
    );

    const handleChange = (color: HSBAColor) => {
        onChange(hsbaToHex(color));
    };

    useEffect(() => {
        setTextColor(value);
    }, [value]);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <div style={{ flexGrow: 1 }}>
                <TextField label={title} autoComplete="off" value={textColor} onChange={handleTextFieldChange} />
            </div>
            <Popover
                active={popoverActive}
                activator={activator}
                autofocusTarget="first-node"
                onClose={togglePopoverActive}
            >
                <ColorPicker onChange={handleChange} color={hexToHsba(value)} allowAlpha={true} />
            </Popover>
        </div>
    );
}
